import React, { useState, useEffect } from 'react';
import { MapPin, Mail, Phone, Facebook, Instagram, X, ChevronUp, ArrowRight, ExternalLink } from 'react-feather';

const brandBlue = '#004e6e';
const brandRed = '#ff4a4a';

const socials = [
  { icon: Facebook, link: "https://www.facebook.com/profile.php?id=61587557248060", label: "Facebook" },
  { icon: Instagram, link: "https://www.instagram.com/sanistyle_gmbh/", label: "Instagram" },
];

const Footer = () => {
  const [legalOpen, setLegalOpen] = useState(null);

  // Handle Scroll Lock & ESC key for Modal
  useEffect(() => {
    if (legalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    const handleEsc = (e) => { if (e.key === "Escape") setLegalOpen(null); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [legalOpen]);

  return (
    <>
      <footer
        id="contact"
        className="relative text-white pb-16 overflow-hidden selection:bg-[#ff4a4a] selection:text-white"
        style={{ backgroundColor: brandBlue }}
      >
        {/* 1. THE WAVE DIVIDER - Placed at top */}
        <div className="absolute top-0 left-0 w-full leading-[0] z-0">
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="#ff4a4a" fill-opacity="1" d="M0,0L1440,32L1440,0L0,0Z"></path></svg>
</div>
        {/* 2. BACKGROUND DECORATION */}
        <div className="absolute top-64 left-1/4 w-[600px] h-[600px] bg-blue-400/5 rounded-full blur-[120px] pointer-events-none"></div>

        {/* 3. MAIN CONTENT CONTAINER - Large padding-top to clear the wave */}
        <div className="relative container mx-auto px-6  z-10 pt-4 md:pt-12">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-16 mb-24">

            {/* BRAND INFO */}
            <div className="lg:col-span-4 space-y-8">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="group inline-block transition-transform duration-500 hover:-rotate-1"
              >
                <img
                  src="/img/white.png"
                  alt="SaniStyle Logo"
                  className="w-[220px] drop-shadow-2xl"
                />
              </button>
              <p className="text-blue-50/70 text-lg leading-relaxed font-light max-w-sm italic">
                "Meisterhafte Präzision in Sanitär & Heizung. Wir definieren Standard neu – mit Schweizer Qualität und Leidenschaft."
              </p>
             
            </div>

            {/* NAVIGATION LINKS */}
            <div className="lg:col-span-2 lg:ml-auto">
              <h4 className="text-[10px] uppercase tracking-[0.4em] text-red-400 font-black mb-8">Navigation</h4>
              <ul className="space-y-5">
                {['Leistungen', 'Über uns', 'Kontakt'].map((item) => (
                  <li key={item}>
                    <a href={`#${item.toLowerCase().replace(' ', '')}`} className="text-blue-50/60 hover:text-white transition-all flex items-center group font-medium">
                      <span className="w-0 group-hover:w-3 h-[2px] bg-red-500 mr-0 group-hover:mr-3 transition-all"></span>
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* CONTACT DETAILS */}
            <div className="lg:col-span-3">
              <h4 className="text-[10px] uppercase tracking-[0.4em] text-red-400 font-black mb-8">Kontakt</h4>
              <div className="space-y-6 text-sm">
                <div className="flex items-start gap-4">
                  <MapPin size={20} className="text-red-500 shrink-0 mt-1" />
                  <span className="text-blue-50/80 leading-relaxed">
                    Solothurnstrasse 46<br />3294 Büren an der Aare
                  </span>
                </div>
                <a href="mailto:info@sani-style.ch" className="flex items-center gap-4 group hover:text-white transition-colors">
                  <Mail size={20} className="text-red-500 shrink-0" />
                  <span className="text-blue-50/80 group-hover:text-white">info@sani-style.ch</span>
                </a>
                <a href="tel:+41787096702" className="flex items-center gap-4 group hover:text-white transition-colors">
                  <Phone size={20} className="text-red-500 shrink-0" />
                  <span className="text-blue-50/80 group-hover:text-white font-bold">+41 78 709 67 02</span>
                </a>
              </div>
            </div>

            {/* CTA CARD */}
            <div className="lg:col-span-3">
              
              <div className="relative p-8 rounded-[2.5rem] bg-white/5 border border-white/10 backdrop-blur-sm overflow-hidden group shadow-2xl">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-red-500/10 rounded-full blur-2xl group-hover:bg-red-500/20 transition-all"></div>
                <h4 className="relative z-10 text-xl font-bold mb-6">Projekt im Kopf?</h4>
                <button 
                  onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
                  className="relative z-10 w-full py-4 bg-white text-[#004e6e] font-black rounded-2xl hover:bg-red-500 hover:text-white transition-all duration-300 flex items-center justify-center gap-2 shadow-xl"
                >
                  Los geht's <ArrowRight size={18} />
                </button>
              </div>
            </div>

          </div>

          {/* BOTTOM BAR */}
          <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-50/30">
             <div className="flex gap-4">
                {socials.map(({ icon: Icon, link, label }, i) => (
                  <a
                    key={i}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white hover:text-[#004e6e] transition-all duration-300 group shadow-lg"
                  >
                    <Icon size={20} className="group-hover:scale-110 transition-transform" />
                  </a>
                ))}
              </div>
            <div className="flex flex-wrap justify-center gap-6">
              <span className="text-blue-50/50 italic">© {new Date().getFullYear()} SaniStyle GmbH</span>
              <button onClick={() => setLegalOpen("datenschutz")} className="hover:text-red-500 transition-colors">Datenschutz</button>
              <button onClick={() => setLegalOpen("impressum")} className="hover:text-red-500 transition-colors">Impressum</button>
              <button onClick={() => setLegalOpen("agb")} className="hover:text-red-500 transition-colors">AGB</button>
            </div>

            <div className="flex items-center gap-8">
               <span className="opacity-50">Crafted by <span className="text-blue-50/80">EnerGenius</span></span>
               <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-red-500 transition-all group shadow-xl"
              >
                <ChevronUp size={20} className="group-hover:-translate-y-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* ======================================================
          THE MODAL SYSTEM (Legal Information)
          ====================================================== */}
      {legalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-hidden"
          onClick={() => setLegalOpen(null)}
        >
          {/* Blur Backdrop */}
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300"></div>

          {/* Modal Content Card */}
          <div
            className="relative bg-white text-slate-900 w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-[2.5rem] shadow-[0_0_80px_rgba(0,0,0,0.5)] flex flex-col animate-in zoom-in-95 slide-in-from-bottom-10 duration-500"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-8 pb-6 flex justify-between items-center border-b border-slate-100">
              <div>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-[#ff4a4a] mb-1 block underline">SaniStyle Dokumentation</span>
                <h2 className="text-4xl font-black text-[#004e6e] tracking-tighter">{legalOpen.toUpperCase()}</h2>
              </div>
              <button
                onClick={() => setLegalOpen(null)}
                className="group p-4 rounded-full bg-slate-100 hover:bg-[#ff4a4a] transition-all duration-300"
              >
                <X size={24} className="group-hover:text-white group-hover:rotate-90 transition-all duration-500" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-10 pt-8 overflow-y-auto custom-scrollbar">
              <div className="space-y-8 text-slate-600 leading-relaxed">
                
                {/* IMPRESSUM CONTENT */}
                {legalOpen === "impressum" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="space-y-2">
                      <h4 className="font-bold text-slate-900 uppercase tracking-widest text-[10px]">Unternehmenssitz</h4>
                      <p className="text-lg">SaniStyle GmbH<br />Solothurnstrasse 46<br />3294 Büren an der Aare<br />Schweiz</p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-bold text-slate-900 uppercase tracking-widest text-[10px]">Kontakt</h4>
                      <p className="text-lg">Tel: +41 78 709 67 02<br />info@sani-style.ch</p>
                    </div>
                    <div className="md:col-span-2 p-6 bg-slate-50 rounded-2xl flex justify-between items-center border border-slate-100">
                      <p className="text-sm">Design & Programmierung:<br /><strong>EnerGenius Agency</strong></p>
                      <a href="https://energeniusagency.com" target="_blank" rel="noreferrer" className="text-[#ff4a4a] hover:underline flex items-center gap-1 font-bold text-sm">Webseite besuchen <ExternalLink size={14}/></a>
                    </div>
                  </div>
                )}

                {/* DATENSCHUTZ CONTENT */}
                {legalOpen === "datenschutz" && (
                  <div className="space-y-6">
                    <p className="text-lg font-medium text-slate-800 italic">"Ihre Privatsphäre ist unser Anliegen."</p>
                    <p>Wir verarbeiten personenbezogene Daten auf dieser Website nur für die Beantwortung Ihrer Anfragen über das Kontaktformular.</p>
                    <p>Es werden keine Cookies für Werbezwecke gespeichert. Ihre Daten werden niemals an Dritte verkauft oder unbefugt weitergegeben.</p>
                  </div>
                )}

                {/* AGB CONTENT */}
                {legalOpen === "agb" && (
                  <div className="space-y-6">
                    <div className="p-5 border-l-4 border-[#ff4a4a] bg-slate-50 rounded-r-2xl">
                       <h5 className="font-bold text-slate-900 mb-2">Geltungsbereich</h5>
                       <p className="text-sm">Sämtliche Dienstleistungen der SaniStyle GmbH unterliegen diesen Geschäftsbedingungen, sofern nicht schriftlich etwas anderes vereinbart wurde.</p>
                    </div>
                    <p className="text-sm">Zahlungsziel: 30 Tage rein netto. Gerichtsstand ist Büren an der Aare, Schweiz.</p>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Scrollbar Styling */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #ff4a4a; }
      `}</style>
    </>
  );
};

export default Footer;